import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Store,
  Eye,
  EyeOff,
  Phone,
  Lock,
  AlertCircle,
  ShieldCheck,
} from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmedPhone = phoneNumber.trim();
    const trimmedPw = password.trim();

    if (!trimmedPhone || !trimmedPw) {
      setError("Please enter your phone number and password.");
      return;
    }
    // Basic length check — accept any format the backend uses
    if (trimmedPhone.length < 7) {
      setError("Enter a valid phone number.");
      return;
    }

    setLoading(true);
    const result = await login(trimmedPhone, trimmedPw);
    setLoading(false);

    if (result.success) {
      navigate(result.role === "tenant" ? "/tenant" : "/dashboard", {
        replace: true,
      });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Left panel – branding */}
      <div className="hidden lg:flex flex-col justify-between w-2/5 xl:w-1/2 bg-gradient-to-br from-gray-900 via-[#12102a] to-gray-950 border-r border-gray-800 p-12">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-glow-violet">
            <Store size={20} className="text-white" />
          </div>
          <div>
            <p className="text-base font-bold text-gray-100">Providha</p>
            <p className="text-xs text-gray-500">Marketplace Platform</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <span className="text-xs font-semibold text-violet-400 uppercase tracking-widest">
              Profiler Portal
            </span>
            <h2 className="text-4xl font-bold text-gray-100 mt-3 leading-tight">
              Manage your
              <br />
              <span className="gradient-text">Lagos business</span>
              <br />
              from one place.
            </h2>
            <p className="text-gray-500 mt-4 text-sm leading-relaxed max-w-sm">
              A powerful multi-tenant marketplace for sellers and tenants.
              Track your products, monitor sales, and grow your market presence.
            </p>
          </div>

          {/* Feature chips */}
          <div className="flex flex-wrap gap-2">
            {[
              "Multi-category listings",
              "Real-time analytics",
              "Featured products",
              "Tenant isolation",
            ].map((f) => (
              <span
                key={f}
                className="text-xs bg-white/5 border border-white/10 text-gray-400 px-3 py-1.5 rounded-full">
                {f}
              </span>
            ))}
          </div>
        </div>

        <p className="text-xs text-gray-700">
          © 2026 Providha. All rights reserved.
        </p>
      </div>

      {/* Right panel – form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-12">
        {/* Mobile logo */}
        <div className="flex items-center gap-2.5 mb-10 lg:hidden">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
            <Store size={18} className="text-white" />
          </div>
          <p className="text-base font-bold text-gray-100">Providha</p>
        </div>

        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-100">Welcome back</h1>
            <p className="text-sm text-gray-500 mt-1.5">
              Sign in to your profiler dashboard
            </p>
          </div>

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <div className="flex items-center gap-2.5 bg-rose-500/10 border border-rose-500/25 text-rose-400 text-sm px-4 py-3 rounded-xl animate-fade-in">
                <AlertCircle size={16} className="flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Phone number
              </label>
              <div className="relative">
                <Phone
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type="tel"
                  className="input-field pl-10"
                  placeholder="08012345678"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  autoComplete="tel"
                />
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Enter the phone number registered on your account.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={15}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type={showPw ? "text" : "password"}
                  className="input-field pl-10 pr-10"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  tabIndex={-1}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                Use the password provided when your account was created.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center py-3 text-base mt-2">
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>Sign in to Dashboard</>
              )}
            </button>
          </form>

          {/* Security note */}
          <div className="mt-8 flex items-start gap-2.5 p-4 rounded-xl bg-gray-900 border border-gray-800">
            <ShieldCheck size={15} className="text-violet-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              Your credentials are sent securely. If you don't have an account,
              contact your market administrator to get set up.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
